import { createFileRoute, retainSearchParams, useParams, Link, useMatch } from '@tanstack/react-router'
import { SectionAssistant } from './-components/assistant/assistant-frame'
import { CRUMB_LINK_CLASS } from './-components/title-section'
import { useSessionUser } from '@/hooks/use-session'
import { useSuspenseQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { Suspense, useDeferredValue, type Ref } from 'react'
import { PlusIcon, FileTextIcon } from 'lucide-react'
import type { RootSearch } from '@/routes/__root'
import { useLingui } from '@lingui/react/macro'
import { useModalLink, useModalMatch } from '@/components/custom/modal-slot'
import { clearProfileSelection, useRootSearch } from '@/hooks/use-root-search'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useHydrationSafePages } from '@/hooks/use-hydrating-render'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { ButtonLink } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { SectionLabel } from '@/components/custom/section-label'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { SectionIconTile } from './-components/shared/section-icon'
import {
  usePreviewMode,
  useIsLivePane,
  NavStackStage,
  NavStackScreen,
  useStackedLayout,
  NS_PASSTHROUGH_CLASS,
} from '@/components/custom/route-transition'
import { SectionStack, useSectionListHidden } from './-components/shared/section-stack'
import { ReportScreenTitle } from './-components/report/report-screen-title'
import { MembersPanelSkeleton, ReportPanelSkeleton } from './-components/app-layout-skeleton'
import { ListBodyPending } from './-components/shared/list-body-pending'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  ScrollableContent,
  SlideInPanel,
  CollapsibleListPanel,
} from './-components/shared/layout'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
} from './-components/shared/collapsible-panel'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint, CollapsibleSectionLabel, CollapsibleToolbar } from './-components/shared/collapsible'
import { PanelEmptyState, PanelSelectionPrompt } from './-components/shared/panel-empty-state'
import { JobTreeItem } from './-components/jobs/job-tree-item'
import { JobActionMenu } from './-components/jobs/job-action-menu'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { IncompleteJobAutoOpen } from './-components/jobs/incomplete-job-auto-open'
import { jobDetail } from './-components/jobs/job-detail'

const JobsEmployeesPanel = jobDetail.pane((panes) => panes.JobsEmployeesPanel)
const JobReportContent = jobDetail.pane((panes) => panes.JobReportContent)
const ReportPager = jobDetail.pane((panes) => panes.ReportPager)

export const Route = createFileRoute('/_app/jobs')({
  search: {
    middlewares: [retainSearchParams<RootSearch>(true)],
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
  // Painted on the click frame; safe only because this match never re-keys — the search-keyed list lives on the
  // pathless `_list` layout below (see jobs._list.tsx), so a keystroke cannot swap this rail for the skeleton.
})

function RouteComponent() {
  const isLivePane = useIsLivePane()
  const { t } = useLingui()
  // The server's nav verdict: an employee in their own view reaches /jobs as their Position.
  const isEmployee = useSessionUser()?.sections.includes('position') ?? false

  // Access (logged-out → /login; self-registered professional → /report) is enforced centrally by the
  // `_app` layout guard (see route-access), before this route's gated `listJobs` query ever mounts.

  // Inside a report-tab live preview (/jobs/<id>/board …), strip the list/report split to just the pager pane.
  if (usePreviewMode('/jobs') === 'passthrough') {
    return (
      <Suspense fallback={null}>
        <ReportPager className="h-full" />
      </Suspense>
    )
  }

  return (
    <div id={isLivePane ? 'report' : undefined} className="@container flex h-full flex-col">
      {/* On a phone the three panes below are STACKED SCREENS, not a squashed row: the jobs list, the job's
          integrants, and the report, each pushed in from the right over the last (see nav-stack.tsx).
          The provider sits above PageLayout so the title bar can carry the stack's back chevron. */}
      <JobsStack>
        <PageLayout
          icon={NAVIGATION_ICONS.jobs}
          title={isEmployee ? t`Position` : t`Jobs`}
          breadcrumbsSlot={<JobsBreadcrumbs />}
          titleActions={<JobsTitleActions />}
        >
          <JobsLayout sectionTitle={isEmployee ? t`Position` : t`Jobs`} />
        </PageLayout>
      </JobsStack>
    </div>
  )
}

/**
 * Jobs' mobile hierarchy — list → the job's integrants → its report. The standard section shape, so the whole
 * declaration is the three routes involved; `SectionStack` owns the depth derivation and the pop policy.
 */
function JobsStack(props: { children: React.ReactNode }) {
  const { jobId } = useParams({ strict: false })
  return (
    <SectionStack scopedEmbed list={{ to: '/jobs' }} detail={jobId ? { to: '/jobs/$jobId', params: { jobId } } : null}>
      {props.children}
    </SectionStack>
  )
}

function JobsLayout(props: { sectionTitle: string }) {
  const { t } = useLingui()
  const { jobId } = useParams({ strict: false })
  const describeRequested = useModalMatch('/@modal/jobs/$jobId/describe/{-$indicator}') !== undefined
  const hasJobSelected = !!jobId
  // A token link / SDK embed opened on one job is scoped to it: no rail, and the job is the stack's root.
  const { listHidden, level } = useSectionListHidden()

  // No viewport-driven auto-collapse: the nav stack gives each pane the WHOLE screen and decides which one is on
  // it, so both providers stay expanded and the collapse is desktop's business only.
  return (
    <CollapsiblePanelProvider defaultCollapsed={false}>
      {/* A describe link (`?@modal=/jobs/5/describe`; the public API's `?describeJob=` redirects there) is an
          explicit request for the wizard; the completion nag would open second, stacking a modal on it. */}
      {jobId && !describeRequested && (
        <Suspense fallback={null}>
          <IncompleteJobAutoOpen />
        </Suspense>
      )}
      {/* The panel row on desktop, the stack's stage on mobile — same div, same classes, see
          NavStackStage. Each pane is wrapped in the level it occupies in the mobile hierarchy; on
          desktop those wrappers are `display: contents` and this lays out exactly as it always did. */}
      <NavStackStage className="flex h-full min-h-0 w-full md:relative" style={{ containerType: 'inline-size' }}>
        {/* LEVEL 0 — Jobs list (always visible on desktop, squashes to a 52px icon column when a job is
            selected; content keeps its full width inside and is clipped so only the avatars show) */}
        {!listHidden && (
          <NavStackScreen
            index={0}
            title={props.sectionTitle}
            leading={<SectionIconTile icon={NAVIGATION_ICONS.jobs} />}
          >
            <CollapsibleListPanel isOpen={hasJobSelected} className="print:hidden">
              <CollapsiblePanel expandedWidth="100%" disableTransition>
                {/* Single-list panel: the list view inside `CollapsiblePanelContent` is a `@container`
                    child of `SquashPanel`, so the same rendered list responsively collapses into a
                    centered avatar column (icon rail) at narrow widths via container queries. No dual
                    rendering — `JobsPanelContent` drives both states. */}
                <CollapsiblePanelContent>
                  <Suspense fallback={<ListBodyPending />}>
                    <JobsPanelContent variant="sidebar" />
                  </Suspense>
                </CollapsiblePanelContent>
              </CollapsiblePanel>
            </CollapsibleListPanel>
          </NavStackScreen>
        )}

        {/* Right Panel Group (Middle + Detail) - width animates from 0 to target. `NS_PASSTHROUGH_CLASS`: on
            mobile it stops being a box so the two levels inside position against the stage, not against
            this group's (0px / clipped) width. */}
        <SlideInPanel isOpen={hasJobSelected} innerClassName="flex" className={NS_PASSTHROUGH_CLASS}>
          {/* LEVEL 1 — the job's integrants. The first screen a job opens onto on mobile. */}
          <NavStackScreen
            index={level(1)}
            title={<JobScreenTitle />}
            actions={jobId ? <JobScreenActions jobId={jobId} /> : null}
          >
            <CollapsiblePanelProvider cookieName="panel_state_middle" keyboardShortcut="]" defaultCollapsed={false}>
              <StickyPanel className="print:hidden">
                <CollapsiblePanel>
                  <Suspense fallback={<MembersPanelSkeleton />}>
                    {jobId ? (
                      <JobsEmployeesPanel jobId={jobId} />
                    ) : (
                      <PanelSelectionPrompt text={t`Select a job to view integrants`} />
                    )}
                  </Suspense>
                </CollapsiblePanel>
              </StickyPanel>
            </CollapsiblePanelProvider>
          </NavStackScreen>

          {/* LEVEL 2 — the report. The `jobs.$jobId` layout route renders here via <AnimatedOutlet/>. It
              lives HERE (not in the `$jobId` layout) so the enter animation plays once when the report panel
              first appears and never re-animates on tab/job switches — see AnimatedOutlet. */}
          <NavStackScreen index={level(2)} title={<ReportScreenTitle />}>
            <ScrollableContent>
              {hasJobSelected ? (
                <Suspense fallback={<ReportPanelSkeleton />}>
                  <JobReportContent />
                </Suspense>
              ) : null}
            </ScrollableContent>
          </NavStackScreen>
        </SlideInPanel>
        {/* Nothing selected: the empty detail side holds the positions' dashboard over the assistant's conversation. */}
        <SectionAssistant shown={!hasJobSelected && !listHidden} listed={!listHidden} />
      </NavStackStage>
    </CollapsiblePanelProvider>
  )
}

function JobsBreadcrumbs() {
  const { t } = useLingui()
  const isEmployee = useSessionUser()?.sections.includes('position') ?? false
  const { jobId } = useParams({ strict: false })
  const { listHidden } = useSectionListHidden()
  const sectionLabel = isEmployee ? t`Position` : t`Jobs`
  // Scoped to one job, there is no list to go back to: the job's own crumb is the whole trail.
  if (listHidden && jobId) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <Suspense fallback={null}>
            <JobDetailCrumb jobId={jobId} leading />
          </Suspense>
        </BreadcrumbList>
      </Breadcrumb>
    )
  }
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className={CRUMB_LINK_CLASS}
            render={
              <Link
                to="/jobs"
                search={(prev) => ({
                  ...clearProfileSelection(prev),
                })}
              >
                {sectionLabel}
              </Link>
            }
          />
        </BreadcrumbItem>
        {jobId && (
          <Suspense fallback={null}>
            <JobDetailCrumb jobId={jobId} />
          </Suspense>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function JobDetailCrumb(props: { jobId: string; leading?: boolean }) {
  // This renders in the LIST layout, above the `$jobId` match, so it has no context of its own for
  // this entity — it reads the OWNING layout's, matched exactly when the id is present, which is the
  // guard every call site already applies.
  const { context } = useMatch({ from: '/_app/jobs/_list/$jobId' })
  const { data: job } = useSuspenseQuery(context.jobQueryOptions)
  if (!job?.name) return null
  return (
    <>
      {!props.leading && <BreadcrumbSeparator />}
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/jobs/$jobId/report"
              params={{ jobId: props.jobId }}
              search={(prev) => ({
                ...clearProfileSelection(prev),
              })}
            >
              {job.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function JobsTitleActions() {
  const { jobId } = useParams({ strict: false })
  if (!jobId) return null
  return (
    <Suspense fallback={null}>
      <JobsTitleActionsLoaded />
    </Suspense>
  )
}

/**
 * The title of the JOB screen (nav stack level 1) — the job's own name, the same thing the desktop
 * breadcrumb's second crumb shows. Suspends on `getJob`, which the `$jobId` layout has already prefetched, so
 * it fills in with the rest of the screen; the fallback keeps the bar's height either way.
 */
function JobScreenTitle() {
  const { jobId } = useParams({ strict: false })
  if (!jobId) return null
  return (
    <Suspense fallback={null}>
      <JobName />
    </Suspense>
  )
}

function JobName() {
  // This renders in the LIST layout, above the `$jobId` match, so it has no context of its own for
  // this entity — it reads the OWNING layout's, matched exactly when the id is present, which is the
  // guard every call site already applies.
  const { context } = useMatch({ from: '/_app/jobs/_list/$jobId' })
  const { data: job } = useSuspenseQuery(context.jobQueryOptions)
  return <>{job?.name ?? ''}</>
}

/**
 * The JOB screen's header actions: the job's own report (one push deeper, the same level a tapped integrant
 * lands on but with the job selected instead of a person — on desktop that pane is always beside the list, so
 * there is nothing to reach for there) and the owner's action menu.
 */
function JobScreenActions(props: { jobId: string }) {
  const { t } = useLingui()
  return (
    <>
      <ButtonLink
        variant="ghost"
        size="icon"
        aria-label={t`Job report`}
        className="size-8"
        to="/jobs/$jobId/report"
        params={{ jobId: props.jobId }}
        search={(prev) => ({ ...prev, second: undefined, selected: 'first' })}
      >
        <FileTextIcon className="size-4" />
      </ButtonLink>
      <Suspense fallback={null}>
        <JobsTitleActionsLoaded />
      </Suspense>
    </>
  )
}

function JobsTitleActionsLoaded() {
  // This renders in the LIST layout, above the `$jobId` match, so it has no context of its own for
  // this entity — it reads the OWNING layout's, matched exactly when the id is present, which is the
  // guard every call site already applies.
  const { context } = useMatch({ from: '/_app/jobs/_list/$jobId' })
  const { data: job } = useSuspenseQuery(context.jobQueryOptions)
  return job.viewerRole === 'owner' ? <JobActionMenu job={job} variant="header" /> : null
}

type PanelJob = {
  id: number
  name: string | null
  custom: { competences: boolean; values: boolean; personality: boolean; all: boolean }
}

// Reads the `listJobs` infinite query the `_list` layout below prefetches and feeds the rail list view.
function JobsPanelContent(props: { variant?: 'sidebar' | 'main' }) {
  const { q: searchQueryFromUrl } = useRootSearch()
  const { searchInput, handleSearchChange } = useDebouncedSearch({ initialValue: searchQueryFromUrl })
  // The `q` that produced the list currently on screen — deferred in lockstep with the options below.
  const deferredQuery = useDeferredValue(searchQueryFromUrl)

  // Deferring the route's OPTIONS object keeps the PREVIOUS list on screen while a new search page loads
  // (keep-previous UX); the staleness drives the search/progress spinner via `isFetching`.
  const { context } = useMatch({ from: '/_app/jobs/_list' })
  const deferredJobs = useDeferredValue(context.jobsListInfiniteQueryOptions)
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(deferredJobs)
  // True while the deferred options lag the live ones (a change is in flight) — keeps the search spinner /
  // fetching bar visible during the keep-previous transition.
  const isJobsFetching = isFetching || deferredJobs !== context.jobsListInfiniteQueryOptions

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  })

  const jobs = useHydrationSafePages(data?.pages ?? []).flatMap((page) => page.list)
  const totalCount = data?.pages[0]?.pagination.count ?? 0
  const firstPage = data?.pages[0]

  return (
    <JobsPanelView
      variant={props.variant}
      jobs={jobs}
      totalCount={totalCount}
      isEmployee={firstPage?.viewerRole === 'member'}
      canCreateJobs={firstPage?.viewerCanCreate ?? false}
      isFetching={isJobsFetching}
      searchInput={searchInput}
      onSearchChange={handleSearchChange}
      loadMoreRef={loadMoreRef}
      isFetchingMore={isFetchingNextPage}
      // The search term that produced the data currently on screen (the DEFERRED `q`, not the live
      // URL one). Empty-state decisions must key off this so clearing the search doesn't briefly flash
      // the cold "no items yet" state (live `q` is already '' while the stale search list is still shown).
      activeQuery={deferredQuery}
    />
  )
}

function JobsPanelView(props: {
  variant?: 'sidebar' | 'main'
  jobs: PanelJob[]
  totalCount: number
  isEmployee: boolean
  canCreateJobs: boolean
  isFetching: boolean
  searchInput: string
  onSearchChange: (value: string) => void
  loadMoreRef?: Ref<HTMLDivElement>
  isFetchingMore?: boolean
  activeQuery: string
}) {
  const { t } = useLingui()
  const isEmployee = props.isEmployee
  const allJobs = props.jobs
  const totalCount = props.totalCount
  const activeQuery = props.activeQuery
  const { jobId } = useParams({ strict: false })
  const { q: searchQueryFromUrl } = useRootSearch()
  const createJobLink = useModalLink('/@modal/jobs/new')
  const findJobLink = useModalLink('/@modal/jobs/find')
  // In a stack this list is a whole level, and a pop always lands on it with the selection cleared — so its
  // hint must be on screen ALREADY while the level above is dragged off it, not appear on the commit and
  // shove the list down a row at the end of the gesture. (The rows' own selected highlight is suppressed
  // inside JobTreeItem — see useStackedSelection.)
  const stacked = useStackedLayout()

  const canCreateJobs = props.canCreateJobs

  const showOnlyEmptyState = totalCount === 0 && !activeQuery

  // Employees have a single assigned position; companies pick from a list — different selection hint.
  const selectionHintText = isEmployee
    ? t`Click on the position to view its report`
    : t`Select a job to view its members and reports`

  const isMainVariant = props.variant === 'main'

  if (isMainVariant) {
    return (
      <div className="flex h-full flex-col">
        {/* Employees only ever have a single assigned position, so the search box is hidden for them. */}
        {!showOnlyEmptyState && !isEmployee && (
          <>
            <div className="flex items-center gap-2 border-b p-3">
              <SearchInput
                placeholder={t`Search jobs...`}
                value={props.searchInput}
                onChange={props.onSearchChange}
                isLoading={props.isFetching && searchQueryFromUrl.length > 0}
              />
              {canCreateJobs && (
                <ButtonLink variant="secondary" {...createJobLink}>
                  <PlusIcon className="size-4" />
                  {t`Add`}
                </ButtonLink>
              )}
            </div>
          </>
        )}

        <ScrollableList
          isFetching={props.isFetching}
          loadMoreRef={props.loadMoreRef}
          isFetchingMore={props.isFetchingMore}
          isEmpty={allJobs.length === 0}
          emptyState={
            <PanelEmptyState
              noResults={allJobs.length === 0 && !!activeQuery}
              icon={<NAVIGATION_ICONS.jobs className="text-muted-foreground size-5" />}
              title={isEmployee ? t`No assigned position yet` : t`No jobs yet`}
              description={isEmployee ? t`Your company has not assigned you a position` : t`Add jobs to get started`}
              action={
                canCreateJobs ? (
                  <ButtonLink variant="secondary" {...createJobLink}>
                    <PlusIcon className="size-4" />
                    {t`Add job`}
                  </ButtonLink>
                ) : undefined
              }
            />
          }
          hint={allJobs.length > 0 && <SelectionHint visible={stacked || !jobId} text={selectionHintText} />}
        >
          <SectionLabel className="px-2">
            {totalCount === 1 ? t`${totalCount} job` : t`${totalCount} jobs`}
          </SectionLabel>
          {allJobs.map((job) => (
            <JobTreeItem key={job.id} job={job} isSelected={jobId === job.id.toString()} hideMenu={isEmployee} />
          ))}
        </ScrollableList>
      </div>
    )
  }

  return (
    <>
      {/* Employees only ever have a single assigned position, so the search box is hidden for them. */}
      {!showOnlyEmptyState && !isEmployee && (
        <>
          <CollapsibleToolbar>
            <CollapsibleToolbar.Search searchLink={findJobLink} searchLabel={t`Search jobs`}>
              <SearchInput
                placeholder={t`Search jobs...`}
                value={props.searchInput}
                onChange={props.onSearchChange}
                isLoading={props.isFetching && searchQueryFromUrl.length > 0}
              />
            </CollapsibleToolbar.Search>
            {canCreateJobs && (
              <CollapsibleToolbar.Action
                icon={<PlusIcon className="size-4" />}
                tooltip={t`Add job`}
                link={createJobLink}
              />
            )}
          </CollapsibleToolbar>
        </>
      )}

      <ScrollableList
        isFetching={props.isFetching}
        loadMoreRef={props.loadMoreRef}
        isFetchingMore={props.isFetchingMore}
        isEmpty={allJobs.length === 0}
        emptyState={
          <PanelEmptyState
            noResults={allJobs.length === 0 && !!activeQuery}
            icon={<NAVIGATION_ICONS.jobs className="text-muted-foreground size-5" />}
            title={isEmployee ? t`No assigned position yet` : t`No jobs yet`}
            description={isEmployee ? t`Your company has not assigned you a position` : t`Add jobs to get started`}
            action={
              canCreateJobs ? (
                <ButtonLink variant="secondary" {...createJobLink}>
                  <PlusIcon className="size-4" />
                  {t`Add job`}
                </ButtonLink>
              ) : undefined
            }
          />
        }
        hint={allJobs.length > 0 && <SelectionHint visible={stacked || !jobId} text={selectionHintText} />}
      >
        <CollapsibleSectionLabel>
          {totalCount === 1 ? t`${totalCount} job` : t`${totalCount} jobs`}
        </CollapsibleSectionLabel>
        {allJobs.map((job) => (
          <JobTreeItem key={job.id} job={job} isSelected={jobId === job.id.toString()} hideMenu={isEmployee} />
        ))}
      </ScrollableList>
    </>
  )
}
